import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Loader2, AlertCircle } from 'lucide-react'
import { getContent, saveContent, type ContentEntity } from '../../lib/api/content'
import { Switch } from './ui'

type El = Record<string, unknown>
const MODEL = 'conventionConfiguration'

const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'
// "10%" / "10" → "10"
const parseDiscount = (v: unknown) => {
  const m = String(v ?? '').match(/(\d+(?:[.,]\d+)?)/)
  return m ? m[1].replace(',', '.') : ''
}
// "2026-04-01T00:00:00Z" → "2026-04-01" (for <input type=date>)
const datePart = (v: unknown) => {
  const s = String(v ?? '')
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : ''
}
// "08:00" / "08:00:00" → "08:00" (for <input type=time>)
const timePart = (v: unknown) => {
  const m = String(v ?? '').match(/^(\d{2}:\d{2})/)
  return m ? m[1] : ''
}

/**
 * Create OR edit a "Fascia sconto" (conventionConfiguration). These are slots in the shared
 * CONVENTION configuration; the backend adapter (ConventionConfigurationAdapter) maps our elements
 * to a slot and assigns a UUID on create / preserves the id on edit, replacing only that one slot.
 * We write exactly what the adapter's content2object expects:
 *   validity.fromDay/toDay = "YYYY-MM-DDT00:00:00Z" (20 chars) | "",  fromTime/toTime = "HH:MM" | ""
 * Edit round-trips the full entity so nothing else is touched.
 */
export function ConventionConfigEditModal({
  editId,
  open,
  onClose,
  listKey,
}: {
  editId: string | null // null = create
  open: boolean
  onClose: () => void
  listKey: readonly unknown[]
}) {
  const qc = useQueryClient()
  const isEdit = Boolean(editId)

  const [name, setName] = useState('')
  const [discount, setDiscount] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [timeFrom, setTimeFrom] = useState('')
  const [timeTo, setTimeTo] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [err, setErr] = useState<string | null>(null)

  // Edit: load the FULL entity so we round-trip (preserve id + any other fields).
  const existing = useQuery({
    queryKey: ['content-one', MODEL, editId],
    queryFn: () => getContent(MODEL, editId!),
    enabled: open && isEdit,
  })

  useEffect(() => {
    if (!open) return
    if (isEdit) {
      if (!existing.data) return
      const el = (existing.data.elements ?? {}) as El
      const val = (el.validity ?? {}) as El
      setName(String(el.name ?? ''))
      setDiscount(parseDiscount(el.discount))
      setDateFrom(datePart(val.fromDay))
      setDateTo(datePart(val.toDay))
      setTimeFrom(timePart(val.fromTime))
      setTimeTo(timePart(val.toTime))
      setEnabled(isYes(el.enabled))
      setErr(null)
    } else {
      setName('')
      setDiscount('')
      setDateFrom('')
      setDateTo('')
      setTimeFrom('')
      setTimeTo('')
      setEnabled(true)
      setErr(null)
    }
  }, [open, isEdit, existing.data])

  const save = useMutation({
    mutationFn: async () => {
      const validity = {
        fromDay: dateFrom ? `${dateFrom}T00:00:00Z` : '',
        toDay: dateTo ? `${dateTo}T00:00:00Z` : '',
        fromTime: timeFrom || '',
        toTime: timeTo || '',
      }
      const elements = { name: name.trim(), discount: discount ? `${discount}%` : '', enabled, validity }
      const entity: ContentEntity =
        isEdit && existing.data
          ? { ...existing.data, elements: { ...(existing.data.elements as El), ...elements } }
          : { id: '', modelId: MODEL, elements }
      return saveContent(MODEL, entity, 'it')
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: listKey })
      onClose()
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Salvataggio non riuscito. Riprova.'),
  })

  if (!open) return null
  const loading = isEdit && existing.isLoading
  const canSave = name.trim().length > 0 && discount.trim().length > 0 && !save.isPending && !loading
  const close = () => {
    if (save.isPending) return
    onClose()
  }
  const fieldCls =
    'h-10 w-full rounded-lg border border-line bg-card px-3 text-[14px] text-coal outline-none focus:border-verdigris'
  const labelCls = 'mb-1 block text-[12px] font-semibold uppercase tracking-wide text-muted'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-coal/40 backdrop-blur-[1px]" onClick={close} />
      <div className="relative w-full max-w-lg overflow-hidden rounded-card border border-line bg-paper shadow-pop">
        <header className="flex items-center justify-between border-b border-line bg-card px-5 py-3.5">
          <h3 className="font-display text-[16px] font-bold text-coal">
            {isEdit ? 'Modifica fascia sconto' : 'Nuova fascia sconto'}
          </h3>
          <button onClick={close} className="rounded-lg p-1.5 text-muted hover:bg-paper hover:text-coal">
            <X className="h-[18px] w-[18px]" />
          </button>
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 px-5 py-4">
            <div>
              <label className={labelCls}>Nome *</label>
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Sconto alta stagione" className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>Sconto (%) *</label>
              <input
                type="number"
                min={0}
                max={100}
                inputMode="decimal"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                placeholder="10"
                className={`${fieldCls} font-mono`}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Data inizio</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Data fine</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Ora inizio</label>
                <input type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Ora fine</label>
                <input type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)} className={fieldCls} />
              </div>
            </div>
            <p className="text-[12px] text-muted">Lascia date/orari vuoti per applicare lo sconto sempre.</p>

            <label className="flex items-center justify-between rounded-lg border border-line bg-card px-3 py-2.5">
              <span className="text-[13px] font-semibold text-slate">Attiva</span>
              <Switch on={enabled} onClick={() => setEnabled((v) => !v)} label="Attiva fascia" />
            </label>

            {err && (
              <p className="flex items-start gap-1.5 text-[12.5px] text-venetian">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {err}
              </p>
            )}
          </div>
        )}

        <footer className="flex items-center justify-end gap-2 border-t border-line bg-card px-5 py-3">
          <button
            onClick={close}
            disabled={save.isPending}
            className="rounded-lg border border-line bg-card px-4 py-2 text-[13px] font-semibold text-slate hover:border-faint disabled:opacity-60"
          >
            Annulla
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={!canSave}
            className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-2 text-[13px] font-semibold text-white hover:bg-verdigris-700 disabled:opacity-60"
          >
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Salva
          </button>
        </footer>
      </div>
    </div>
  )
}
